// Matches DBMGate.tsx's LOCAL_SESSION_KEY — the same signed token used to
// prove who's asking, reused here so the upload endpoint can't be used to
// set someone else's avatar.
const LOCAL_SESSION_KEY = "dbm-local-session-token";

const UPLOAD_URL = process.env.REACT_APP_UPLOAD_AVATAR_URL || "/.netlify/functions/dbm-upload-avatar";

const AVATAR_SIZE = 320;

/**
 * Resizes and center-crops an image file down to a small square JPEG —
 * keeps the upload fast and the result crisp as an avatar, regardless of
 * whether the original was a 12MP phone photo or a tiny screenshot.
 */
function resizeToSquareJpeg(file: File): Promise<{ base64: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = AVATAR_SIZE;
      canvas.height = AVATAR_SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Your browser can't process images here"));
        return;
      }
      // Cover-crop: scale so the SHORTER side fills the square, centering
      // the overflow — same behavior as CSS object-fit: cover.
      const scale = AVATAR_SIZE / Math.min(img.width, img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (AVATAR_SIZE - w) / 2, (AVATAR_SIZE - h) / 2, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      const base64 = dataUrl.split(",")[1] || "";
      resolve({ base64, contentType: "image/jpeg" });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't read that image — try a different file"));
    };
    img.src = url;
  });
}

/** Resizes the file client-side, uploads it, and returns the new avatar
 * URL once CometChat's own record has been updated to point at it. */
export async function uploadAvatarPhoto(file: File): Promise<string> {
  const token = localStorage.getItem(LOCAL_SESSION_KEY);
  if (!token) throw new Error("Your session expired — sign out and back in, then try again");

  const { base64, contentType } = await resizeToSquareJpeg(file);

  const res = await fetch(UPLOAD_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, imageBase64: base64, contentType }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.avatarUrl) {
    throw new Error(data.error || "Couldn't upload the photo — try again");
  }
  return data.avatarUrl as string;
}
